import * as React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
  ArrowLeft as ArrowLeftIcon,
  CalendarBlank as CalendarIcon,
  Clock as ClockIcon,
  Copy as CopyIcon,
  CurrencyDollar as CurrencyDollarIcon,
  DotsThreeVertical as DotsIcon,
  File as FileIcon,
  Gavel as GavelIcon,
  PencilSimple as PencilIcon,
  Receipt as ReceiptIcon,
  Trash as TrashIcon,
  User as UserIcon,
  UsersThree as UsersIcon,
  XCircle as CancelIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate, useParams } from 'react-router-dom';

import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import {
  approveReservation,
  cancelReservation,
  deleteCommunityReservation,
  getCommunityReservation,
  refundDeposit,
  refundFee,
  updateCommunityReservation,
} from '@/api/reservations';
import { getS3PresignedUrlFromPath } from '@/utils/s3';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';
import { useUser } from '@/hooks/v2/use-user';
import { hasAnyAccountRoles, hasCommunityRolesV2 } from '@/utils/has-roles';
import { paths } from '@/paths';
import { toast } from '@/components/core/v2/toaster';

dayjs.extend(utc);

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'default',
  completed: 'info',
  no_show: 'error',
  waitlisted: 'info',
};

function ReservationDocumentsCard({
  documents,
}: {
  documents: NonNullable<import('@/types/v2/reservation').AmenityReservation['documents']>;
}): React.JSX.Element {
  const [presignedUrls, setPresignedUrls] = React.useState<Record<string, string>>({});
  const [loadingId, setLoadingId] = React.useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewType, setPreviewType] = React.useState<string | null>(null);

  const handlePreview = async (doc: NonNullable<import('@/types/v2/reservation').AmenityReservation['documents']>[0]) => {
    const cached = presignedUrls[doc.id];
    if (cached) {
      setPreviewUrl(cached);
      setPreviewType(doc.file_name ?? doc.file_key);
      return;
    }
    setLoadingId(doc.id);
    try {
      const url = await getS3PresignedUrlFromPath(doc.file_key);
      setPresignedUrls((prev) => ({ ...prev, [doc.id]: url }));
      setPreviewUrl(url);
      setPreviewType(doc.file_name ?? doc.file_key);
    } finally {
      setLoadingId(null);
    }
  };

  const isImage = (name?: string) => /\.(jpe?g|png|gif|webp)$/i.test(name ?? '');
  const isPdf = (name?: string) => /\.pdf$/i.test(name ?? '');

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          avatar={<FileIcon size={20} weight="duotone" />}
          title="Submitted Documents"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={1.5}>
            {documents.map((doc) => (
              <Box
                key={doc.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {doc.document_type}
                  </Typography>
                  {doc.file_name && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {doc.file_name}
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handlePreview(doc)}
                  disabled={loadingId === doc.id}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  {loadingId === doc.id ? 'Loading…' : 'Preview'}
                </Button>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Inline preview */}
      {previewUrl && (
        <Card variant="outlined">
          <CardHeader
            title={previewType ?? 'Document Preview'}
            titleTypographyProps={{ variant: 'subtitle2' }}
            action={
              <Button size="small" variant="text" onClick={() => setPreviewUrl(null)}>
                Close
              </Button>
            }
          />
          <Divider />
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {isImage(previewType ?? '') ? (
              <Box
                component="img"
                src={previewUrl}
                alt={previewType ?? 'document'}
                sx={{ width: '100%', maxHeight: 600, objectFit: 'contain', display: 'block' }}
              />
            ) : isPdf(previewType ?? '') ? (
              <Box
                component="iframe"
                src={previewUrl}
                sx={{ width: '100%', height: 600, border: 'none', display: 'block' }}
                title="Document Preview"
              />
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Preview not available for this file type.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open File
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}

function StatBox({
  icon,
  label,
  value,
  sublabel,
  isLastInRow,
  isLastInColumn,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sublabel?: React.ReactNode;
  isLastInRow?: boolean;
  isLastInColumn?: boolean;
}): React.JSX.Element {
  return (
    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: 1.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        borderRight: { sm: isLastInRow ? 'none' : '1px solid' },
        borderBottom: { xs: isLastInColumn ? 'none' : '1px solid', sm: 'none' },
        borderColor: { xs: 'divider', sm: 'divider' },
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
        {icon}
        <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="body1" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
        {value}
      </Typography>
      {sublabel ? (
        <Typography variant="caption" color="text.secondary" component="div">
          {sublabel}
        </Typography>
      ) : null}
    </Box>
  );
}

function CopyableId({ id }: { id: string }): React.JSX.Element {
  const handleCopy = () => {
    try {
      void navigator.clipboard.writeText(id);
      toast.success('Transaction ID copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' }}
      >
        {id}
      </Typography>
      <Tooltip title="Copy">
        <IconButton size="small" onClick={handleCopy} sx={{ flexShrink: 0 }}>
          <CopyIcon size={14} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

export function ReservationDetailPage(): React.JSX.Element {
  const { reservationId } = useParams<{ reservationId: string }>();
  const { account, community } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = React.useState(false);
  const [feeRefundDialogOpen, setFeeRefundDialogOpen] = React.useState(false);
  const [editErrors, setEditErrors] = React.useState<Record<string, string>>({});
  const [editForm, setEditForm] = React.useState({
    start_time: '',
    end_time: '',
    purpose: '',
    special_requests: '',
    guest_count: '',
  });
  const [cancelReason, setCancelReason] = React.useState('');
  const [refundAmount, setRefundAmount] = React.useState('');
  const [refundReason, setRefundReason] = React.useState('');
  const [feeRefundReason, setFeeRefundReason] = React.useState('');
  const [actionMenuAnchor, setActionMenuAnchor] = React.useState<null | HTMLElement>(null);

  const hasAccountReservationAccess = hasAnyAccountRoles(account!, [
    ACCOUNT_ROLES.ACCOUNT_OWNER,
    ACCOUNT_ROLES.ACCOUNT_ADMIN,
    ACCOUNT_ROLES.ACCOUNT_MANAGER,
  ]);
  const hasCommunityReservationAdminAccess = hasCommunityRolesV2(community, [
    COMMUNITY_ROLES.COMMUNITY_MANAGER,
  ]);
  const hasBoardReservationAccess = hasCommunityRolesV2(community, [
    COMMUNITY_ROLES.BOARD_MEMBER,
  ]);
  const canEditReservations =
    hasAccountReservationAccess ||
    hasCommunityReservationAdminAccess ||
    hasBoardReservationAccess;
  // Board members administer reservations like managers (product decision), so
  // this now matches canEditReservations. Without board here the UI was
  // incoherent: a board member could refund a FEE (canEditReservations) but not
  // a DEPOSIT (canAdministerReservations), while the API allowed both.
  // Note the deliberate exception elsewhere: Stripe Connect onboarding and the
  // transactions feed remain manager/account-only.
  const canAdministerReservations =
    hasAccountReservationAccess || hasCommunityReservationAdminAccess || hasBoardReservationAccess;

  const { data: reservation, isLoading, error } = useQuery({
    queryKey: ['community-reservation', community?.id, reservationId],
    queryFn: () => getCommunityReservation(community?.id ?? '', reservationId ?? '', { include: 'user,amenity,unit,documents' }),
    enabled: !!community?.id && !!reservationId,
    retry: (failureCount, err: any) => {
      // Don't retry on 404 or 403 — the answer won't change.
      const status = err?.response?.status;
      if (status === 404 || status === 403) return false;
      return failureCount < 2;
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      updateCommunityReservation(community?.id ?? '', id, data),
    onError: (err: any) => {
      const responseData = err?.response?.data;
      if (err?.response?.status === 422 && responseData?.errors) {
        setEditErrors(responseData.errors);
        toast.error('Please fix the validation errors below');
      } else {
        setEditErrors({});
        toast.error(responseData?.message || 'Failed to update reservation');
      }
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => approveReservation(community?.id ?? '', id, {}),
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to approve reservation');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      cancelReservation(id, { cancellation_reason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-reservation', community?.id, reservationId] });
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      toast.success('Reservation cancelled successfully');
      setCancelDialogOpen(false);
      setCancelReason('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to cancel reservation');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCommunityReservation(community?.id ?? '', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      toast.success('Reservation deleted successfully');
      navigate(paths.settings.community.reservations.index);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to delete reservation');
    },
  });

  const refundMutation = useMutation({
    mutationFn: ({ id, amount, reason }: { id: string; amount?: number; reason?: string }) =>
      refundDeposit(community?.id ?? '', id, { amount, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-reservation', community?.id, reservationId] });
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      toast.success('Deposit refunded successfully');
      setRefundDialogOpen(false);
      setRefundAmount('');
      setRefundReason('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to refund deposit');
    },
  });

  const feeRefundMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      refundFee(community?.id ?? '', id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-reservation', community?.id, reservationId] });
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      toast.success('Fee refunded successfully');
      setFeeRefundDialogOpen(false);
      setFeeRefundReason('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to refund fee');
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !reservation) {
    const is404 = (error as any)?.response?.status === 404;
    const is403 = (error as any)?.response?.status === 403;
    return (
      <Box>
        <Button
          startIcon={<ArrowLeftIcon size={20} />}
          variant="text"
          onClick={() => navigate(paths.settings.community.reservations.index)}
          sx={{ mb: 2 }}
        >
          Back to Reservations
        </Button>
        {is404 ? (
          <Alert severity="warning">
            This reservation could not be found. It may have been deleted, or it may belong to a different community.
          </Alert>
        ) : is403 ? (
          <Alert severity="error">
            You do not have permission to view this reservation.
          </Alert>
        ) : (
          <Alert severity="error">Reservation not found or could not be loaded.</Alert>
        )}
      </Box>
    );
  }

  const status = reservation.status as string;
  const hasRefund = !!reservation.deposit_refunded_at;
  const hasFeeRefund = !!reservation.fee_refunded_at;
  const hasDamage = !!reservation.damage_charge_amount;
  const isPast = dayjs.utc(reservation.start_time).local().isBefore(dayjs());
  const hasReservationPayment = Boolean(
    reservation.deposit_collected ||
      reservation.fee_collected ||
      reservation.total_fee_collected ||
      reservation.deposit_paid_at ||
      reservation.fee_paid_at ||
      reservation.total_fee_paid_at ||
      reservation.stripe_deposit_payment_intent_id ||
      reservation.stripe_fee_payment_intent_id ||
      reservation.deposit_refunded_at ||
      reservation.deposit_refund_id ||
      reservation.fee_refunded_at ||
      reservation.fee_refund_id,
  );
  const canCancelThisReservation =
    canAdministerReservations &&
    !isPast &&
    !['cancelled', 'rejected', 'no_show'].includes(status) &&
    (hasReservationPayment || ['approved', 'completed'].includes(status));
  const canEditThisReservation = canEditReservations && !isPast && !hasRefund;
  const canApproveThisReservation =
    canEditReservations && !isPast && ['pending', 'rejected', 'cancelled'].includes(status);
  const canDeleteThisReservation = canAdministerReservations && status === 'pending' && !isPast && !hasReservationPayment;
  const canRefund = canAdministerReservations && !!reservation.stripe_deposit_payment_intent_id && !reservation.deposit_refunded_at;
  const canRefundFee = canEditReservations && !!reservation.stripe_fee_payment_intent_id && !reservation.fee_refunded_at;

  const openEditDialog = () => {
    setEditForm({
      start_time: dayjs.utc(reservation.start_time).local().format('YYYY-MM-DD HH:mm:ss'),
      end_time: dayjs.utc(reservation.end_time).local().format('YYYY-MM-DD HH:mm:ss'),
      purpose: reservation.purpose || '',
      special_requests: reservation.special_requests || '',
      guest_count: reservation.guest_count?.toString() || '',
    });
    setEditErrors({});
    setEditDialogOpen(true);
  };

  const getEditPayload = () => {
    const data: any = {};
    if (editForm.start_time !== dayjs.utc(reservation.start_time).local().format('YYYY-MM-DD HH:mm:ss')) {
      data.start_time = dayjs(editForm.start_time).utc().format('YYYY-MM-DD HH:mm:ss');
    }
    if (editForm.end_time !== dayjs.utc(reservation.end_time).local().format('YYYY-MM-DD HH:mm:ss')) {
      data.end_time = dayjs(editForm.end_time).utc().format('YYYY-MM-DD HH:mm:ss');
    }
    if (editForm.purpose !== reservation.purpose) data.purpose = editForm.purpose;
    if (editForm.special_requests !== reservation.special_requests) data.special_requests = editForm.special_requests;
    if (editForm.guest_count !== reservation.guest_count?.toString()) {
      data.guest_count = editForm.guest_count ? parseInt(editForm.guest_count) : null;
    }
    return data;
  };

  const handleEditSubmit = async ({ approveAfterSave = false }: { approveAfterSave?: boolean } = {}) => {
    const data = getEditPayload();

    try {
      if (Object.keys(data).length > 0) {
        await updateMutation.mutateAsync({ id: reservation.id, data });
      }

      if (approveAfterSave) {
        await approveMutation.mutateAsync(reservation.id);
        toast.success('Reservation updated and approved');
      } else if (Object.keys(data).length > 0) {
        toast.success('Reservation updated successfully');
      } else {
        toast.info('No reservation changes to save');
      }

      queryClient.invalidateQueries({ queryKey: ['community-reservation', community?.id, reservationId] });
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      setEditDialogOpen(false);
    } catch {
      // Mutation error handlers show details.
    }
  };

  const handleRefundSubmit = () => {
    const amount = refundAmount ? parseFloat(refundAmount) : undefined;
    if (amount !== undefined && (isNaN(amount) || amount <= 0)) {
      toast.error('Please enter a valid refund amount');
      return;
    }
    const depositCharged = Number(reservation.deposit_amount_charged ?? 0);
    if (amount !== undefined && amount > depositCharged) {
      toast.error(`Refund amount cannot exceed the collected deposit of $${depositCharged.toFixed(2)}`);
      return;
    }
    refundMutation.mutate({
      id: reservation.id,
      amount,
      reason: refundReason || undefined,
    });
  };

  const handleFeeRefundSubmit = () => {
    feeRefundMutation.mutate({
      id: reservation.id,
      reason: feeRefundReason || undefined,
    });
  };

  const handleApproveReservation = async () => {
    try {
      await approveMutation.mutateAsync(reservation.id);
      queryClient.invalidateQueries({ queryKey: ['community-reservation', community?.id, reservationId] });
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      toast.success(status === 'pending' ? 'Reservation approved' : 'Reservation re-approved');
    } catch {
      // Mutation error handler shows details.
    }
  };

  const handleCancelSubmit = () => {
    cancelMutation.mutate({
      id: reservation.uuid ?? reservation.id,
      reason: cancelReason.trim() || undefined,
    });
  };

  const shortId = (reservation.uuid ?? reservation.id)?.substring(0, 8).toUpperCase();
  const startLocal = dayjs.utc(reservation.start_time).local();
  const endLocal = dayjs.utc(reservation.end_time).local();
  const durationMin = endLocal.diff(startLocal, 'minute');
  const durationStr =
    durationMin >= 60
      ? `${(durationMin / 60).toFixed(durationMin % 60 === 0 ? 0 : 1)} hr`
      : `${durationMin} min`;
  const totalChargedNum = Number(reservation.total_fee_amount_charged ?? 0);
  const depositChargedNum = Number(reservation.deposit_amount_charged ?? 0);
  const feeChargedNum = Number(reservation.fee_amount_charged ?? 0);
  const sumCharged =
    totalChargedNum > 0 ? totalChargedNum : depositChargedNum + feeChargedNum;
  const paymentSummary = (() => {
    if (sumCharged <= 0) return '—';
    if (hasRefund && hasFeeRefund) return 'Fully refunded';
    if (hasRefund) return 'Deposit refunded';
    if (hasFeeRefund) return 'Fee refunded';
    if (reservation.deposit_collected && reservation.fee_collected) return 'Paid in full';
    if (reservation.deposit_collected) return 'Deposit paid';
    if (reservation.fee_collected) return 'Fee paid';
    return 'Awaiting payment';
  })();
  const residentDisplay = reservation.user
    ? `${reservation.user.first_name} ${reservation.user.last_name}`
    : null;

  type SecondaryAction = {
    key: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    danger?: boolean;
  };
  const candidateActions: Array<SecondaryAction | null> = [
    canEditThisReservation
      ? {
          key: 'edit',
          label: 'Edit Reservation',
          icon: <PencilIcon size={18} />,
          onClick: openEditDialog,
        }
      : null,
    canApproveThisReservation
      ? {
          key: 'approve',
          label: status === 'pending' ? 'Approve Reservation' : 'Re-approve Reservation',
          icon: <GavelIcon size={18} />,
          onClick: () => {
            void handleApproveReservation();
          },
        }
      : null,
    canRefund
      ? {
          key: 'refund-deposit',
          label: 'Refund Deposit',
          icon: <ReceiptIcon size={18} />,
          onClick: () => setRefundDialogOpen(true),
        }
      : null,
    canRefundFee
      ? {
          key: 'refund-fee',
          label: 'Refund Fee',
          icon: <ReceiptIcon size={18} />,
          onClick: () => setFeeRefundDialogOpen(true),
        }
      : null,
    canCancelThisReservation
      ? {
          key: 'cancel',
          label: 'Cancel Reservation',
          icon: <CancelIcon size={18} />,
          onClick: () => setCancelDialogOpen(true),
        }
      : null,
    canDeleteThisReservation
      ? {
          key: 'delete',
          label: 'Delete Reservation',
          icon: <TrashIcon size={18} />,
          danger: true,
          onClick: () => setDeleteDialogOpen(true),
        }
      : null,
  ];
  const secondaryActions: SecondaryAction[] = candidateActions.filter(
    (action): action is SecondaryAction => action !== null,
  );
  const hasSecondaryActions = secondaryActions.length > 0;

  return (
    <Stack spacing={3}>
      {/* Back button */}
      <Box>
        <Button
          startIcon={<ArrowLeftIcon size={20} />}
          variant="text"
          onClick={() => navigate(paths.settings.community.reservations.index)}
        >
          Back to Reservations
        </Button>
      </Box>

      {/* Hero header */}
      <Card variant="outlined">
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Avatar
                variant="rounded"
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'primary.softBg',
                  color: 'primary.main',
                }}
              >
                <CalendarIcon size={28} weight="duotone" />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h5" fontWeight={700} noWrap>
                    {reservation.amenity?.name ?? 'Reservation'}
                  </Typography>
                  <Chip
                    label={status.replace('_', ' ').toUpperCase()}
                    size="small"
                    color={statusColors[status] ?? 'default'}
                    sx={{ fontWeight: 700, letterSpacing: 0.4 }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {residentDisplay ? <>For <strong>{residentDisplay}</strong> · </> : null}
                  Reservation #{shortId}
                </Typography>
              </Box>
            </Stack>

            {hasSecondaryActions && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="More actions">
                  <IconButton
                    onClick={(event) => setActionMenuAnchor(event.currentTarget)}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    <DotsIcon size={20} weight="bold" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={actionMenuAnchor}
                  open={Boolean(actionMenuAnchor)}
                  onClose={() => setActionMenuAnchor(null)}
                  slotProps={{ paper: { sx: { minWidth: 220 } } }}
                >
                  {secondaryActions.map((action) => (
                    <MenuItem
                      key={action.key}
                      onClick={() => {
                        setActionMenuAnchor(null);
                        action.onClick();
                      }}
                      sx={action.danger ? { color: 'error.main' } : undefined}
                    >
                      <ListItemIcon sx={action.danger ? { color: 'error.main' } : undefined}>
                        {action.icon}
                      </ListItemIcon>
                      <ListItemText primary={action.label} />
                    </MenuItem>
                  ))}
                </Menu>
              </Stack>
            )}
          </Stack>
        </Box>

        <Divider />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          }}
        >
          <StatBox
            icon={<CalendarIcon size={14} weight="duotone" />}
            label="Date"
            value={startLocal.format('MMM D, YYYY')}
            sublabel={startLocal.format('dddd')}
          />
          <StatBox
            icon={<ClockIcon size={14} weight="duotone" />}
            label="Time"
            value={`${startLocal.format('h:mm A')} – ${endLocal.format('h:mm A')}`}
            sublabel={durationStr}
          />
          <StatBox
            icon={<UsersIcon size={14} weight="duotone" />}
            label="Guests"
            value={reservation.guest_count ?? '—'}
            sublabel={reservation.alcohol_served ? 'Alcohol served' : undefined}
            isLastInColumn
          />
          <StatBox
            icon={<CurrencyDollarIcon size={14} weight="duotone" />}
            label="Charges"
            value={sumCharged > 0 ? `$${sumCharged.toFixed(2)}` : '—'}
            sublabel={paymentSummary}
            isLastInRow
            isLastInColumn
          />
        </Box>
      </Card>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        {/* Reservation Info */}
        <Card variant="outlined">
          <CardHeader
            avatar={<CalendarIcon size={20} weight="duotone" />}
            title="Booking Details"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
          />
          <Divider />
          <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '10px 16px' }}>
            <PropertyItem name="Amenity" value={reservation.amenity?.name ?? '—'} />
            <PropertyItem name="Purpose" value={reservation.purpose ?? '—'} />
            {reservation.special_requests && (
              <PropertyItem name="Special Requests" value={reservation.special_requests} />
            )}
            {reservation.alcohol_served && <PropertyItem name="Alcohol Served" value="Yes" />}
            <PropertyItem
              name="Requested"
              value={dayjs(reservation.created_at).format('MMM D, YYYY h:mm A')}
            />
          </PropertyList>
        </Card>

        {/* Resident Info */}
        <Card variant="outlined">
          <CardHeader
            avatar={<UserIcon size={20} weight="duotone" />}
            title="Resident"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
          />
          <Divider />
          <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '10px 16px' }}>
            <PropertyItem
              name="Name"
              value={reservation.user ? `${reservation.user.first_name} ${reservation.user.last_name}` : '—'}
            />
            <PropertyItem
              name="Email"
              value={reservation.user?.email ?? '—'}
            />
            {reservation.unit && (
              <PropertyItem
                name="Unit"
                value={reservation.unit.unit_number ?? reservation.unit.name}
              />
            )}
          </PropertyList>
        </Card>

        {/* Payment / Deposit */}
        {(() => {
          const totalCharged = Number(reservation.total_fee_amount_charged ?? 0);
          const depositCharged = Number(reservation.deposit_amount_charged ?? 0);
          const feeCharged = Number(reservation.fee_amount_charged ?? 0);
          const remainingFee = feeCharged > 0
            ? (feeCharged <= totalCharged && depositCharged > 0 ? Math.max(0, totalCharged - depositCharged) : feeCharged)
            : 0;
          const hasDeposit = depositCharged > 0;
          const hasFee = remainingFee > 0;
          const paidInFull = hasDeposit && hasFee && !!reservation.deposit_collected && !!reservation.fee_collected;
          const hasPaymentInfo = hasDeposit || hasFee || totalCharged > 0;

          if (!hasPaymentInfo) return null;

          return (
            <Card variant="outlined" sx={paidInFull ? { borderColor: 'success.main' } : {}}>
              <CardHeader
                avatar={<CurrencyDollarIcon size={20} weight="duotone" color={paidInFull ? 'var(--mui-palette-success-main)' : undefined} />}
                title="Payment"
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                action={paidInFull ? (
                  <Chip label="Paid in Full" size="small" color="success" sx={{ fontWeight: 600, mr: 1 }} />
                ) : undefined}
              />
              <Divider />
              <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '10px 16px' }}>
                {totalCharged > 0 && (
                  <PropertyItem
                    name="Total Fee"
                    value={
                      <Typography variant="body2" fontWeight={600}>
                        ${totalCharged.toFixed(2)}
                      </Typography>
                    }
                  />
                )}
                {hasDeposit && (
                  <PropertyItem
                    name="Deposit"
                    value={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="body2"
                          sx={hasRefund ? { textDecoration: 'line-through', color: 'text.disabled' } : {}}
                        >
                          ${depositCharged.toFixed(2)}
                        </Typography>
                        {hasRefund ? (
                          <Chip label="Refunded" size="small" color="info" />
                        ) : reservation.deposit_collected ? (
                          <Chip label="Paid" size="small" color="success" />
                        ) : (
                          <Chip label="Outstanding" size="small" color="warning" variant="outlined" />
                        )}
                      </Stack>
                    }
                  />
                )}
                {reservation.deposit_paid_at && !hasRefund && (
                  <PropertyItem
                    name="Deposit Paid On"
                    value={dayjs(reservation.deposit_paid_at).format('MMM D, YYYY')}
                  />
                )}
                {reservation.stripe_deposit_payment_intent_id && (
                  <PropertyItem
                    name="Deposit Transaction"
                    value={<CopyableId id={reservation.stripe_deposit_payment_intent_id} />}
                  />
                )}
                {hasFee && (
                  <PropertyItem
                    name="Remaining Fee"
                    value={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="body2"
                          sx={hasFeeRefund ? { textDecoration: 'line-through', color: 'text.disabled' } : {}}
                        >
                          ${remainingFee.toFixed(2)}
                        </Typography>
                        {hasFeeRefund ? (
                          <Chip label="Refunded" size="small" color="info" />
                        ) : reservation.fee_collected ? (
                          <Chip label="Paid" size="small" color="success" />
                        ) : (
                          <Chip label="Outstanding" size="small" color="warning" variant="outlined" />
                        )}
                      </Stack>
                    }
                  />
                )}
                {reservation.fee_paid_at && (
                  <PropertyItem
                    name="Fee Paid On"
                    value={dayjs(reservation.fee_paid_at).format('MMM D, YYYY')}
                  />
                )}
                {reservation.stripe_fee_payment_intent_id && (
                  <PropertyItem
                    name="Fee Transaction"
                    value={<CopyableId id={reservation.stripe_fee_payment_intent_id} />}
                  />
                )}
              </PropertyList>
            </Card>
          );
        })()}

        {/* Refund Details */}
        {hasRefund && (
          <Card variant="outlined" sx={{ borderColor: 'info.main' }}>
            <CardHeader
              avatar={<CurrencyDollarIcon size={20} weight="duotone" color="var(--mui-palette-info-main)" />}
              title="Refund"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600, color: 'info.main' }}
            />
            <Divider />
            <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '10px 16px' }}>
              <PropertyItem
                name="Refund Amount"
                value={
                  <Typography variant="body2" fontWeight={600} color="info.main">
                    ${Number(reservation.deposit_refund_amount ?? reservation.deposit_amount_charged ?? 0).toFixed(2)}
                  </Typography>
                }
              />
              <PropertyItem
                name="Refunded On"
                value={dayjs(reservation.deposit_refunded_at).format('MMM D, YYYY h:mm A')}
              />
              {reservation.deposit_refund_id && (
                <PropertyItem
                  name="Stripe Refund ID"
                  value={<CopyableId id={reservation.deposit_refund_id} />}
                />
              )}
              {reservation.deposit_refund_reason && (
                <PropertyItem
                  name="Reason"
                  value={reservation.deposit_refund_reason}
                />
              )}
            </PropertyList>
          </Card>
        )}

        {/* Fee Refund Details */}
        {hasFeeRefund && (
          <Card variant="outlined" sx={{ borderColor: 'info.main' }}>
            <CardHeader
              avatar={<CurrencyDollarIcon size={20} weight="duotone" color="var(--mui-palette-info-main)" />}
              title="Fee Refund"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600, color: 'info.main' }}
            />
            <Divider />
            <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '10px 16px' }}>
              <PropertyItem
                name="Refund Amount"
                value={
                  <Typography variant="body2" fontWeight={600} color="info.main">
                    ${Number(reservation.fee_refund_amount ?? reservation.fee_amount_charged ?? 0).toFixed(2)}
                  </Typography>
                }
              />
              <PropertyItem
                name="Refunded On"
                value={dayjs(reservation.fee_refunded_at).format('MMM D, YYYY h:mm A')}
              />
              {reservation.fee_refund_id && (
                <PropertyItem
                  name="Stripe Refund ID"
                  value={<CopyableId id={reservation.fee_refund_id} />}
                />
              )}
              {reservation.fee_refund_reason && (
                <PropertyItem
                  name="Reason"
                  value={reservation.fee_refund_reason}
                />
              )}
            </PropertyList>
          </Card>
        )}

        {/* Damage Charge */}
        {hasDamage && (
          <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
            <CardHeader
              avatar={<GavelIcon size={20} weight="duotone" color="var(--mui-palette-error-main)" />}
              title="Damage Charge"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600, color: 'error.main' }}
            />
            <Divider />
            <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '10px 16px' }}>
              <PropertyItem
                name="Amount"
                value={
                  <Typography variant="body2" fontWeight={600} color="error.main">
                    ${Number(reservation.damage_charge_amount).toFixed(2)}
                  </Typography>
                }
              />
              {reservation.damage_charged_at && (
                <PropertyItem
                  name="Charged On"
                  value={dayjs(reservation.damage_charged_at).format('MMM D, YYYY h:mm A')}
                />
              )}
              {reservation.damage_charge_notes && (
                <PropertyItem
                  name="Notes"
                  value={reservation.damage_charge_notes}
                />
              )}
            </PropertyList>
          </Card>
        )}

        {/* Submitted Documents */}
        {reservation.documents && reservation.documents.length > 0 && (
          <ReservationDocumentsCard documents={reservation.documents} />
        )}

        {/* Rejection / Cancellation */}
        {(reservation.rejected_reason || reservation.cancellation_reason) && (
          <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
            <CardContent>
              {reservation.rejected_reason && (
                <>
                  <Typography variant="subtitle2" fontWeight={600} color="error" gutterBottom>
                    Rejection Reason
                  </Typography>
                  <Typography variant="body2">{reservation.rejected_reason}</Typography>
                </>
              )}
              {reservation.cancellation_reason && (
                <>
                  <Typography variant="subtitle2" fontWeight={600} color="error" gutterBottom>
                    Cancellation Reason
                  </Typography>
                  <Typography variant="body2">{reservation.cancellation_reason}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Reservation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <DateTimePicker
              label="Start Time"
              value={dayjs(editForm.start_time)}
              onChange={(newValue) => {
                if (newValue) {
                  setEditForm((prev) => ({ ...prev, start_time: newValue.format('YYYY-MM-DD HH:mm:ss') }));
                  if (editErrors.start_time) setEditErrors((prev) => ({ ...prev, start_time: '' }));
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(editErrors.start_time),
                  helperText: editErrors.start_time,
                },
              }}
            />
            <DateTimePicker
              label="End Time"
              value={dayjs(editForm.end_time)}
              onChange={(newValue) => {
                if (newValue) {
                  setEditForm((prev) => ({ ...prev, end_time: newValue.format('YYYY-MM-DD HH:mm:ss') }));
                  if (editErrors.end_time) setEditErrors((prev) => ({ ...prev, end_time: '' }));
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(editErrors.end_time),
                  helperText: editErrors.end_time,
                },
              }}
            />
            <TextField
              label="Purpose"
              multiline
              rows={2}
              value={editForm.purpose}
              onChange={(event) => {
                setEditForm((prev) => ({ ...prev, purpose: event.target.value }));
                if (editErrors.purpose) setEditErrors((prev) => ({ ...prev, purpose: '' }));
              }}
              fullWidth
              error={Boolean(editErrors.purpose)}
              helperText={editErrors.purpose}
            />
            <TextField
              label="Special Requests"
              multiline
              rows={3}
              value={editForm.special_requests}
              onChange={(event) => {
                setEditForm((prev) => ({ ...prev, special_requests: event.target.value }));
                if (editErrors.special_requests) setEditErrors((prev) => ({ ...prev, special_requests: '' }));
              }}
              fullWidth
              error={Boolean(editErrors.special_requests)}
              helperText={editErrors.special_requests}
            />
            <TextField
              label="Guest Count"
              type="number"
              value={editForm.guest_count}
              onChange={(event) => {
                setEditForm((prev) => ({ ...prev, guest_count: event.target.value }));
                if (editErrors.guest_count) setEditErrors((prev) => ({ ...prev, guest_count: '' }));
              }}
              fullWidth
              inputProps={{ min: 0 }}
              error={Boolean(editErrors.guest_count)}
              helperText={editErrors.guest_count}
            />
            <Alert severity="info">
              <strong>Resident:</strong> {reservation.user?.first_name} {reservation.user?.last_name}
              <br />
              <strong>Amenity:</strong> {reservation.amenity?.name}
              <br />
              <strong>Final time:</strong> {dayjs(editForm.start_time).format('MMM D, YYYY h:mm A')} - {dayjs(editForm.end_time).format('h:mm A')}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          {status === 'pending' && !isPast && (
            <Button
              onClick={() => {
                void handleEditSubmit({ approveAfterSave: true });
              }}
              variant="contained"
              color="success"
              disabled={updateMutation.isPending || approveMutation.isPending}
            >
              {updateMutation.isPending || approveMutation.isPending ? 'Saving...' : 'Save & Approve'}
            </Button>
          )}
          <Button
            onClick={() => {
              void handleEditSubmit();
            }}
            variant="outlined"
            disabled={updateMutation.isPending || approveMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Reservation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              This will cancel the reservation and keep the payment history attached to it. Process any needed refunds separately.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {reservation.amenity?.name} for {reservation.user?.first_name} {reservation.user?.last_name} on{' '}
              {dayjs.utc(reservation.start_time).local().format('MMM D, YYYY h:mm A')}.
            </Typography>
            <TextField
              label="Cancellation Reason"
              multiline
              rows={3}
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep Reservation</Button>
          <Button
            onClick={handleCancelSubmit}
            variant="contained"
            color="warning"
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Reservation'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Reservation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              This is only available for reservations with no payment activity. This action cannot be undone.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {reservation.amenity?.name} for {reservation.user?.first_name} {reservation.user?.last_name} on{' '}
              {dayjs.utc(reservation.start_time).local().format('MMM D, YYYY h:mm A')}.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => deleteMutation.mutate(reservation.id)}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Reservation'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Refund Deposit</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              This refunds the deposit transaction only. The amenity fee transaction, if present, is not included in this action.
            </Alert>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.default',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Deposit transaction to refund
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5 }}>
                {reservation.stripe_deposit_payment_intent_id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Collected deposit: ${Number(reservation.deposit_amount_charged ?? 0).toFixed(2)}
              </Typography>
            </Box>
            <TextField
              label="Refund Amount ($) — leave blank for full refund"
              type="number"
              value={refundAmount}
              onChange={(event) => setRefundAmount(event.target.value)}
              fullWidth
              inputProps={{ min: 0.01, max: Number(reservation.deposit_amount_charged ?? 0), step: 0.01 }}
              helperText={`Maximum refund: $${Number(reservation.deposit_amount_charged ?? 0).toFixed(2)}`}
            />
            <TextField
              label="Reason (optional)"
              multiline
              rows={2}
              value={refundReason}
              onChange={(event) => setRefundReason(event.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRefundSubmit} variant="contained" color="info" disabled={refundMutation.isPending}>
            {refundMutation.isPending ? 'Refunding...' : 'Issue Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={feeRefundDialogOpen} onClose={() => setFeeRefundDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Refund Fee</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              This refunds the full fee transaction only. The deposit transaction, if present, is not included in this action.
            </Alert>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.default',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Fee transaction to refund
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5 }}>
                {reservation.stripe_fee_payment_intent_id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Full fee refund: ${Number(reservation.fee_amount_charged ?? 0).toFixed(2)}
              </Typography>
            </Box>
            <TextField
              label="Reason (optional)"
              multiline
              rows={2}
              value={feeRefundReason}
              onChange={(event) => setFeeRefundReason(event.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeeRefundDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleFeeRefundSubmit} variant="contained" color="info" disabled={feeRefundMutation.isPending}>
            {feeRefundMutation.isPending ? 'Refunding...' : 'Refund Full Fee'}
          </Button>
        </DialogActions>
      </Dialog>

    </Stack>
  );
}
